import type { RouteRecord } from 'vite-react-ssg';
import { Navigate } from 'react-router-dom';
import AppLayout from './App';
import RouteErrorFallback from './components/RouteErrorFallback';
import { RETIRED_REDIRECTS } from './lib/locale-routes';

const RELOAD_FLAG = 'chunk-reload-attempted';

// After a deploy, an open tab still references old hashed chunks; the dynamic
// import then fails. Reload once (guarded by sessionStorage) to fetch the new
// index.html; if it fails again, rethrow so the errorElement shows.
const lazyDefault = (loader: () => Promise<{ default: React.ComponentType<any> }>) =>
  async () => {
    try {
      const mod = await loader();
      if (typeof window !== 'undefined') sessionStorage.removeItem(RELOAD_FLAG);
      return { Component: mod.default };
    } catch (error) {
      if (typeof window !== 'undefined' && !sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw error;
    }
  };

export const routes: RouteRecord[] = [
  {
    path: '/',
    Component: AppLayout,
    errorElement: <RouteErrorFallback />,
    children: [
      { index: true, lazy: lazyDefault(() => import('./pages/Index')) },
      { path: 'thank-you', lazy: lazyDefault(() => import('./pages/ThankYou')) },

      { path: 'blog', lazy: lazyDefault(() => import('./pages/blog/BlogIndex')) },
      { path: 'blog/automate-manual-data-entry', lazy: lazyDefault(() => import('./pages/blog/AutomateDataEntry')) },
      { path: 'blog/3-quick-wins-automation', lazy: lazyDefault(() => import('./pages/blog/ThreeQuickWins')) },
      { path: 'blog/how-it-works', lazy: lazyDefault(() => import('./pages/blog/HowItWorks')) },
      { path: 'blog/what-is-geo-optimization', lazy: lazyDefault(() => import('./pages/blog/WhatIsGeoOptimization')) },
      { path: 'blog/what-is-vibe-coding', lazy: lazyDefault(() => import('./pages/blog/WhatIsVibeCoding')) },
      { path: 'blog/what-is-an-ai-consultant', lazy: lazyDefault(() => import('./pages/blog/WhatIsAnAIConsultant')) },
      { path: 'blog/what-is-agentic-ai', lazy: lazyDefault(() => import('./pages/blog/WhatIsAgenticAI')) },
      { path: 'blog/ai-for-small-business-marketing', lazy: lazyDefault(() => import('./pages/blog/AiForSmallBusinessMarketing')) },
      { path: 'blog/ai-agents-for-small-business', lazy: lazyDefault(() => import('./pages/blog/AiAgentsForSmallBusiness')) },
      { path: 'blog/ai-automation-in-healthcare', lazy: lazyDefault(() => import('./pages/blog/AiAutomationInHealthcare')) },
      { path: 'blog/ai-automation-examples', lazy: lazyDefault(() => import('./pages/blog/AiAutomationExamples')) },

      { path: 'privacy-policy', lazy: lazyDefault(() => import('./pages/PrivacyPolicy')) },
      { path: 'es/privacy-policy', lazy: lazyDefault(() => import('./pages/es/PrivacyPolicy')) },

      { path: 'about', lazy: lazyDefault(() => import('./pages/About')) },
      { path: 'es/about', lazy: lazyDefault(() => import('./pages/es/About')) },

      { path: 'how-we-work', lazy: lazyDefault(() => import('./pages/HowWeWork')) },
      { path: 'es/como-trabajamos', lazy: lazyDefault(() => import('./pages/es/HowWeWork')) },

      // Retired service pages: client-side mirror of the middleware 301s.
      ...Object.entries(RETIRED_REDIRECTS).map(([from, to]) => ({
        path: from.slice(1),
        element: <Navigate to={to} replace />,
      })),

      { path: 'case-studies', lazy: lazyDefault(() => import('./pages/CaseStudies')) },
      { path: 'case-studies/cantabrico', lazy: lazyDefault(() => import('./pages/CaseStudyCantabrico')) },
      { path: 'case-studies/splenserendo', lazy: lazyDefault(() => import('./pages/CaseStudySplenserendo')) },
      { path: 'es/case-studies', lazy: lazyDefault(() => import('./pages/es/CaseStudies')) },
      { path: 'es/case-studies/cantabrico', lazy: lazyDefault(() => import('./pages/es/CaseStudyCantabrico')) },
      { path: 'es/case-studies/splenserendo', lazy: lazyDefault(() => import('./pages/es/CaseStudySplenserendo')) },


      { path: 'es', lazy: lazyDefault(() => import('./pages/es/Index')) },


      { path: 'es/blog', lazy: lazyDefault(() => import('./pages/es/Blog')) },
      { path: 'es/blog/how-it-works', lazy: lazyDefault(() => import('./pages/es/blog/HowItWorks')) },
      { path: 'es/blog/automate-manual-data-entry', lazy: lazyDefault(() => import('./pages/es/blog/AutomateManualDataEntry')) },
      { path: 'es/blog/3-quick-wins-automation', lazy: lazyDefault(() => import('./pages/es/blog/ThreeQuickWinsAutomation')) },
      { path: 'es/blog/what-is-geo-optimization', lazy: lazyDefault(() => import('./pages/es/blog/WhatIsGeoOptimization')) },
      { path: 'es/blog/what-is-vibe-coding', lazy: lazyDefault(() => import('./pages/es/blog/WhatIsVibeCoding')) },
      { path: 'es/blog/what-is-an-ai-consultant', lazy: lazyDefault(() => import('./pages/es/blog/WhatIsAnAiConsultant')) },
      { path: 'es/blog/what-is-agentic-ai', lazy: lazyDefault(() => import('./pages/es/blog/WhatIsAgenticAI')) },
      { path: 'es/blog/ai-for-small-business-marketing', lazy: lazyDefault(() => import('./pages/es/blog/AiForSmallBusinessMarketing')) },
      { path: 'es/blog/ai-agents-for-small-business', lazy: lazyDefault(() => import('./pages/es/blog/EsAiAgentsForSmallBusiness')) },
      { path: 'es/blog/ai-automation-in-healthcare', lazy: lazyDefault(() => import('./pages/es/blog/EsAiAutomationInHealthcare')) },
      { path: 'es/blog/ai-automation-examples', lazy: lazyDefault(() => import('./pages/es/blog/EsAiAutomationExamples')) },

      { path: 'tools/flight-search', lazy: lazyDefault(() => import('./pages/tools/FlightSearch')) },

      { path: 'feed', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'feed/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'wp-admin/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'wp-content/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'wp-includes/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'wp-login.php', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'xmlrpc.php', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'category/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: '2024/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: '2025/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'branding-1', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'building', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'logo/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'image/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'comments/*', lazy: lazyDefault(() => import('./pages/NotFound')) },
      { path: 'comments/feed', lazy: lazyDefault(() => import('./pages/NotFound')) },

      { path: '*', lazy: lazyDefault(() => import('./pages/NotFound')) },
    ],
  },
];

export default routes;